import type React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface BarSeries {
  key: string;
  name: string;
  color: string;
}

interface BarChartWidgetProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  bars: BarSeries[];
  height?: number;
  yFormatter?: (value: number) => string;
}

export function BarChartWidget({
  data,
  xKey,
  bars,
  height = 280,
  yFormatter,
}: BarChartWidgetProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No data available for selected filters
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,16%,90%)" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
          tickFormatter={(v) => (yFormatter ? yFormatter(v) : String(v))}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid hsl(210,16%,85%)",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(value) => {
            if (typeof value === "number" && yFormatter) return yFormatter(value);
            return value as React.ReactNode;
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {bars.map((b) => (
          <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
